import { useMemo } from 'react';
import { Button } from '@shared/ui';
import { toNumberOrNull } from '@shared/utils';
import Select from '@repo/select';
import { useSections } from '@modules/sections';
import { useCategoriesGroupedBySection } from '@modules/categories';
import type { Category } from '@modules/categories';
import { IoAdd, IoClose } from 'react-icons/io5';
import './CategorySelection.css';

export interface CategorySelectionItem {
    sectionId: number | '';
    categoryId: number | '';
}

export interface CategorySelectionProps {
    value?: CategorySelectionItem[];
    onChange?: (value: CategorySelectionItem[]) => void;
    error?: string;
    label?: string;
}

export default function CategorySelection({
    value = [{ sectionId: '', categoryId: '' }],
    onChange,
    error,
    label = 'Категории *',
}: CategorySelectionProps) {
    const { data: sections = [] } = useSections();
    const { data: categoriesGroupedBySection = {} } = useCategoriesGroupedBySection();

    const categorySelections = value;

    const sectionOptions = useMemo(() => {
        return sections.length > 0
            ? sections.map((section) => ({
                value: section.id.toString(),
                label: section.name,
            }))
            : [{ value: '', label: 'Нет секций', disabled: true }];
    }, [sections]);

    const updateSelections = (newSelections: CategorySelectionItem[]) => {
        if (onChange) {
            onChange(newSelections);
        }
    };

    const handleSectionChange = (index: number, sectionValue: number | null) => {
        const newSelections = [...categorySelections];
        newSelections[index] = {
            sectionId: sectionValue || '',
            categoryId: '',
        };
        updateSelections(newSelections);
    };

    const handleCategoryChange = (index: number, categoryValue: number | null) => {
        const newSelections = [...categorySelections];
        newSelections[index] = {
            ...newSelections[index],
            categoryId: categoryValue || '',
        };
        updateSelections(newSelections);
    };

    const handleAddCategory = () => {
        const newSelections: CategorySelectionItem[] = [...categorySelections, { sectionId: '', categoryId: '' }];
        updateSelections(newSelections);
    };

    const handleRemoveCategory = (index: number) => {
        if (categorySelections.length > 1) {
            const newSelections = categorySelections.filter((_, i) => i !== index);
            updateSelections(newSelections);
        }
    };

    // Вычисляем опции категорий для всех секций заранее
    const categoryOptionsBySection = useMemo(() => {
        const options: Record<number, Array<{ value: string; label: string; disabled?: boolean }>> = {};

        Object.keys(categoriesGroupedBySection).forEach((sectionIdStr) => {
            const sectionId = Number(sectionIdStr);
            // JSON всегда возвращает строковые ключи, поэтому используем строковый ключ для доступа
            const categories = (categoriesGroupedBySection as any)[sectionIdStr] || [];

            if (categories.length === 0) {
                options[sectionId] = [{ value: '', label: 'Нет категорий в этой секции', disabled: true }];
            } else {
                options[sectionId] = categories.map((category: Category) => ({
                    value: category.id.toString(),
                    label: category.name,
                }));
            }
        });

        return options;
    }, [categoriesGroupedBySection]);

    const getCategoryOptions = (
        sectionId: number | '',
        currentIndex: number
    ): Array<{ value: string; label: string; disabled?: boolean }> => {
        if (!sectionId) {
            return [{ value: '', label: 'Сначала выберите секцию', disabled: true }];
        }

        const baseOptions = categoryOptionsBySection[sectionId] || [
            { value: '', label: 'Нет категорий в этой секции', disabled: true },
        ];

        // Получаем все выбранные категории из других инпутов (кроме текущего)
        const selectedCategoryIds = categorySelections
            .map((sel, idx) => (idx !== currentIndex && sel.categoryId !== '' ? sel.categoryId : null))
            .filter((id): id is number => id !== null)
            .map((id) => id.toString());

        // Помечаем категории как disabled, если они уже выбраны в других инпутах
        return baseOptions.map((option) => {
            if (option.value === '' || option.disabled) {
                return option;
            }
            return {
                ...option,
                disabled: selectedCategoryIds.includes(option.value),
            };
        });
    };

    return (
        <div className="category-selection">
            <label>{label}</label>
            {categorySelections.map((selection, index) => {
                const categoryOptions = getCategoryOptions(selection.sectionId, index);

                return (
                    <div key={index} className="category-selection-row">
                        <div className="form-group" style={{ flex: 1 }}>
                            <Select
                                placeholder="Выберите секцию"
                                data={sectionOptions}
                                value={selection.sectionId === '' ? null : selection.sectionId}
                                onChange={(value) => handleSectionChange(index, toNumberOrNull(value))}
                                clearable
                                nothingFound="Ничего не найдено"
                            />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <Select
                                placeholder={!selection.sectionId ? "Сначала выберите секцию" : "Выберите категорию"}
                                data={categoryOptions}
                                value={selection.categoryId === '' ? null : selection.categoryId}
                                onChange={(value) => handleCategoryChange(index, toNumberOrNull(value))}
                                disabled={!selection.sectionId}
                                clearable
                                nothingFound="Ничего не найдено"
                            />
                        </div>
                        {categorySelections.length > 1 && (
                            <Button
                                variant="secondary-icon"
                                type="button"
                                onClick={() => handleRemoveCategory(index)}
                            >
                                <IoClose />
                            </Button>
                        )}
                        {index === categorySelections.length - 1 && (
                            <Button variant="secondary-icon" type="button" onClick={handleAddCategory}>
                                <IoAdd />
                            </Button>
                        )}
                    </div>
                );
            })}
            {error && <span className="error-message">{error}</span>}
        </div>
    );
}

